import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { notification } from 'antd';

import BouncedEmailsContext from '@totem/components/administration/bouncedEmails/BouncedEmailsContext';
import {
  BouncedEmail,
  BouncedEmailFilterOptions,
  BouncedEmailsReport,
} from '@totem/components/administration/bouncedEmails/types';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import {
  BOUNCED_EMAILS_ENDPOINT,
  BOUNCED_EMAILS_FILTERS_ENDPOINT,
} from '@totem/utilities/endpoints';

const toFilterOptions = (values?: string[]) =>
  (values ?? []).map((value) => ({ text: value, value }));

type Props = {
  children: ReactNode;
};

const BouncedEmailsContainer = ({ children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const {
    buildParameters,
    setFilterOptions,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
    isReady,
  } = useContext(InputContext);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<BouncedEmail[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [refreshData, setRefreshData] = useState(true);

  useEffect(() => {
    fetch(BOUNCED_EMAILS_FILTERS_ENDPOINT, {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
    })
      .then((res) => res.json())
      .then((options: BouncedEmailFilterOptions) => {
        setFilterOptions('status', toFilterOptions(options?.statuses));

        setFilterOptions(
          'organization',
          toFilterOptions(options?.organizations),
        );
      })
      .catch(() => {
        notification.error({
          message: 'Failed to load bounced email filter options.',
        });
      });
  }, [getApiToken]);

  useEffect(() => {
    if (isReady) {
      setRefreshData(true);
    }
  }, [isReady, filterUpdateCount, sortingUpdateCount, pagingUpdateCount]);

  useEffect(() => {
    if (!isReady || !refreshData) {
      return;
    }

    setLoading(true);

    const params: Params = buildParameters();

    fetch(BOUNCED_EMAILS_ENDPOINT, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(params),
    })
      .then((res) => res.json())
      .then((result: BouncedEmailsReport) => {
        setData(result?.results ?? []);

        setTotalRecords(result?.paging?.totalRecords ?? 0);
      })
      .catch(() => {
        notification.error({ message: 'Failed to load bounced emails.' });
      })
      .finally(() => setLoading(false));

    setRefreshData(false);
  }, [refreshData, isReady, getApiToken]);

  return (
    <BouncedEmailsContext.Provider
      value={{
        loading,
        data,
        totalRecords,
        onRefresh: () => setRefreshData(true),
      }}
    >
      {children}
    </BouncedEmailsContext.Provider>
  );
};

export default BouncedEmailsContainer;
